// Spec 45 component-behavior — cmp_ds_breadcrumb + aria_breadcrumb.
//
// nav > ol > li sequence per WAI-ARIA breadcrumb pattern. The CURRENT
// page (last entry) carries aria-current='page' + renders as plain
// text (not a link) — clicking the current page is a no-op affordance
// that confuses screenreaders.
//
// Separator between items is decorative — wrap in span aria-hidden so
// screenreaders don't read "slash slash slash". Default separator is
// "/" but customizable via `separator` prop.
//
// Truncation: when items > maxVisible, collapse middle into a "…"
// disclosure (revealed via Menu in a future Adım; Phase 4c.1 ships
// the static collapsed marker only — caller can opt-out via
// maxVisible=Infinity).

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export interface BreadcrumbItem {
  readonly label: string;
  /** Omit href on the LAST item — current page is plain text per ARIA pattern. */
  readonly href?: string;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  readonly items: readonly BreadcrumbItem[];
  /** Visible items max — when items.length exceeds, middle collapses to "…". Defaults to all visible. */
  readonly maxVisible?: number;
  /** Decorative separator between items. Defaults to "/". */
  readonly separator?: ReactNode;
  /** Required accessible label for the nav landmark. */
  readonly "aria-label"?: string;
  /**
   * Builder for the visually-hidden screenreader announcement when middle
   * items collapse into "…". Receives the count of hidden items so callers
   * can pluralize via ICU MF2 (per ADR-006). Defaults to an English phrase
   * suitable for development.
   */
  readonly hiddenItemsLabel?: (hiddenCount: number) => string;
}

interface CollapsedItem {
  readonly kind: "collapsed";
}
interface RealItem {
  readonly kind: "item";
  readonly item: BreadcrumbItem;
  readonly index: number;
}

function buildVisibleItems(
  items: readonly BreadcrumbItem[],
  maxVisible: number,
): readonly (RealItem | CollapsedItem)[] {
  if (items.length <= maxVisible) {
    return items.map((item, index) => ({ kind: "item" as const, item, index }));
  }
  // Always keep first + last; collapse the middle.
  // `maxVisible` counts ITEMS shown (not the ellipsis marker). With
  // maxVisible=4 you get: first + ellipsis + (maxVisible-1=3) tail
  // items — total 4 items + 1 ellipsis. The first item is always
  // shown so tail = maxVisible - 1.
  const out: (RealItem | CollapsedItem)[] = [
    { kind: "item", item: items[0]!, index: 0 },
    { kind: "collapsed" },
  ];
  const tailCount = Math.max(1, maxVisible - 1);
  const tailStart = items.length - tailCount;
  for (let i = tailStart; i < items.length; i += 1) {
    out.push({ kind: "item", item: items[i]!, index: i });
  }
  return out;
}

const defaultHiddenItemsLabel = (count: number): string =>
  `Hidden ${count} intermediate breadcrumb item${count === 1 ? "" : "s"}`;

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      maxVisible = Number.POSITIVE_INFINITY,
      separator = "/",
      "aria-label": ariaLabel = "Breadcrumb",
      hiddenItemsLabel = defaultHiddenItemsLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    if (isDevGuardActive() && items.length === 0) {
      throw new Error("<Breadcrumb> requires at least one item");
    }

    const visible = buildVisibleItems(items, maxVisible);
    // Hidden = total items minus what we render visibly. With maxVisible=N
    // we render: first item + (maxVisible-1) tail items = N items. So
    // hidden = items.length - maxVisible. NOTE: when items.length <=
    // maxVisible we never enter the collapsed branch, so this expression
    // is only consumed for the truncated case where the result is > 0.
    const hiddenCount = items.length - maxVisible;

    return (
      <nav
        ref={ref}
        aria-label={ariaLabel}
        data-component="breadcrumb"
        className={cx("nai-breadcrumb", className)}
        {...rest}
      >
        <ol data-component="breadcrumb-list" className="nai-breadcrumb-list">
          {visible.map((entry, position) => {
            const isLastVisible = position === visible.length - 1;
            if (entry.kind === "collapsed") {
              return (
                <li key="collapsed" data-component="breadcrumb-collapsed">
                  <span aria-hidden>…</span>
                  <span className="visually-hidden">
                    {hiddenItemsLabel(hiddenCount)}
                  </span>
                  {!isLastVisible && (
                    <span data-component="breadcrumb-separator" aria-hidden>
                      {separator}
                    </span>
                  )}
                </li>
              );
            }
            const isCurrent = entry.index === items.length - 1;
            return (
              <li key={`${entry.index}-${entry.item.label}`} data-component="breadcrumb-item">
                {isCurrent || entry.item.href === undefined ? (
                  <span
                    data-component="breadcrumb-current"
                    aria-current={isCurrent ? "page" : undefined}
                  >
                    {entry.item.label}
                  </span>
                ) : (
                  <a
                    href={entry.item.href}
                    data-component="breadcrumb-link"
                    className="nai-breadcrumb-link"
                  >
                    {entry.item.label}
                  </a>
                )}
                {!isLastVisible && (
                  <span data-component="breadcrumb-separator" aria-hidden>
                    {separator}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  },
);
Breadcrumb.displayName = "Breadcrumb";
